/*
  COREEX announcement bar -- infinite marquee
  Progressive enhancement over the CSS in section-coreex-announcement-bar.css.

  The CSS alone already loops seamlessly for content wider than the viewport
  (which covers most promo lines). This script makes it bulletproof:
    1. Constant scroll speed (px/second) regardless of message length/count.
    2. Short content: repeats the whole group (all messages, in order) so the
       loop stays seamless (no empty gap) instead of leaving dead space.
    3. Re-measures on resize, orientation change, font load, and theme-editor reloads.

  UPDATE (multi-message):
    - A group can now contain MULTIPLE `.coreex-marquee__item` elements (one
      per message block) instead of always exactly one. fillGroup() used to
      clone a single "base" item; it now clones the group's whole original
      item set together, so short combined content repeats as a unit
      ("Msg A • Msg B • Msg A • Msg B ...") rather than just cloning item #1.
    - Previously this script only ever measured/animated under
      `(max-width: 740px)`, because the CSS only enabled the marquee mechanism
      on mobile. Sections with 2+ messages now run the marquee mechanism (via
      .coreex-announcement-bar--marquee-multi) at every breakpoint, so the
      mobile-only gate here has been removed — the mqMobile check is gone,
      layout() now runs on desktop too. It's a no-op on desktop for
      single-message bars because the CSS for those overrides the marquee
      back to a static block regardless of animation-duration.

  If this file fails to load, the CSS fallback (20s linear loop) still works.
*/
(function () {
  'use strict';

  var SELECTOR = '.coreex-announcement-bar--marquee .coreex-marquee';
  var mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clearClones(track) {
    var clones = track.querySelectorAll('.coreex-marquee__item--clone');
    for (var i = 0; i < clones.length; i++) {
      clones[i].parentNode.removeChild(clones[i]);
    }
  }

  // Repeat the group's original item set (in order) until the group is at
  // least as wide as the bar, so one copy alone fills the screen and the
  // -50% loop has no dead space. Works for a group with one item (legacy
  // single-message bars) or several (multi-message bars) alike.
  function fillGroup(group, minWidth) {
    var originals = [];
    var children = group.children;
    for (var c = 0; c < children.length; c++) {
      if (!children[c].classList.contains('coreex-marquee__item--clone')) {
        originals.push(children[c]);
      }
    }
    if (!originals.length) return;

    var guard = 0;
    while (group.scrollWidth < minWidth && guard < 30) {
      for (var i = 0; i < originals.length; i++) {
        var clone = originals[i].cloneNode(true);
        clone.classList.add('coreex-marquee__item--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        group.appendChild(clone);
      }
      guard++;
    }
  }

  function layout(marquee) {
    var track = marquee.querySelector('.coreex-marquee__track');
    if (!track) return;

    // Always reset to the clean 2-copy state first.
    clearClones(track);
    track.style.animationDuration = '';

    if (mqReduced.matches) return;

    var barWidth = marquee.clientWidth;
    if (!barWidth) return;

    // Each direct child of the track is one repeat group; keep both identical.
    var groups = track.children;
    for (var i = 0; i < groups.length; i++) {
      fillGroup(groups[i], barWidth);
    }

    var half = track.scrollWidth / 2; // distance travelled by the -50% keyframe
    if (half <= 0) return;

    var speed = parseFloat(marquee.getAttribute('data-speed')) || 50; // px per second
    var duration = half / speed;
    if (duration < 4) duration = 4;
    if (duration > 120) duration = 120;
    track.style.animationDuration = duration.toFixed(2) + 's';
  }

  function layoutAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) layout(nodes[i]);
  }

  var raf;
  function schedule() {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function () {
      layoutAll(document);
    });
  }

  function bindMQ(mq) {
    if (mq.addEventListener) mq.addEventListener('change', schedule);
    else if (mq.addListener) mq.addListener(schedule); // older Safari
  }

  function init() {
    layoutAll(document);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    bindMQ(mqReduced);
    // Re-measure once the custom heading font swaps in (widths shift otherwise).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        layoutAll(document);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-layout when this section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    layoutAll(e.target);
  });
})();